import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { formatPrice } from "@/utils/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, ChevronDown, FileDown, Printer, RefreshCw, Trash2, Search, Globe, Loader2, Package, Image as ImageIcon, AlertTriangle, Copy } from "lucide-react";
import { ProductFormDialog } from "@/components/menu/ProductFormDialog";
import { ProductShareLink, useRestaurantMenuLinkBase } from "@/components/menu/ProductShareLink";
import { BulkImageUpload } from "@/components/menu/BulkImageUpload";
import { CloneMenuDialog } from "@/components/menu/CloneMenuDialog";
import { ALLERGENS_DATA } from "@/components/menu/allergensData";
import { API_URL } from "@/utils/apiConfig";

export function ProductosTab({ products, setProducts, categories, onRefresh }) {
  const { authAxios, user } = useAuth();
  const menuLinkBase = useRestaurantMenuLinkBase(authAxios, user?.restaurant_id);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showCloneMenu, setShowCloneMenu] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);

  const getCatName = (catId) => categories.find(c => c.id === catId)?.name || "-";

  const filtered = products.filter(p => {
    if (catFilter !== "all" && p.category_id !== catFilter) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const toggleForSale = async (product) => {
    const newVal = !product.for_sale;
    setProducts(prev => prev.map(p => p.id === product.id ? { ...p, for_sale: newVal } : p));
    try {
      await authAxios.put(`/menu/products/${product.id}`, { ...product, for_sale: newVal, category_id: product.category_id });
      toast.success(newVal ? "Activado" : "Desactivado");
    } catch {
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, for_sale: product.for_sale } : p));
      toast.error("Error");
    }
  };

  const toggleVariation = async (product) => {
    const newVal = !product.has_variation;
    setProducts(prev => prev.map(p => p.id === product.id ? { ...p, has_variation: newVal } : p));
    try {
      await authAxios.put(`/menu/products/${product.id}`, { ...product, has_variation: newVal, category_id: product.category_id });
      toast.success("Actualizado");
    } catch {
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, has_variation: product.has_variation } : p));
      toast.error("Error");
    }
  };

  const deleteProduct = async (id) => {
    setProducts(prev => prev.filter(p => p.id !== id));
    try {
      await authAxios.delete(`/menu/products/${id}`);
      toast.success("Eliminado");
    } catch {
      onRefresh();
      toast.error("Error");
    }
  };

  const exportCSV = () => {
    const header = "Nombre,Codigo,Categoria,Cantidad,A la venta,Variacion,Precio\n";
    const rows = filtered.map(p => `${p.name},${p.code || ""},${getCatName(p.category_id)},${p.quantity || 0},${p.for_sale ? "Si" : "No"},${p.has_variation ? "Si" : "No"},${p.price}`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "productos.csv"; a.style.display = "none"; document.body.appendChild(a); a.click(); setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
    toast.success("CSV exportado");
  };

  return (
    <div data-testid="productos-tab">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 font-bold" data-testid="acciones-btn">ACCIONES <ChevronDown className="w-4 h-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuItem onClick={() => { setEditProduct(null); setShowForm(true); }} data-testid="action-new-product"><Plus className="w-4 h-4 mr-2" /> Nuevo producto</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowBulkUpload(true)} data-testid="action-bulk-images"><ImageIcon className="w-4 h-4 mr-2" /> Subir fotos en masa</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowCloneMenu(true)} data-testid="action-clone-menu"><Globe className="w-4 h-4 mr-2" /> Clonar menu de URL</DropdownMenuItem>
            <DropdownMenuItem data-testid="action-bulk-update"><RefreshCw className="w-4 h-4 mr-2" /> Actualizar em masa</DropdownMenuItem>
            <DropdownMenuItem onClick={exportCSV} data-testid="action-export-csv"><FileDown className="w-4 h-4 mr-2" /> Exportar para CSV</DropdownMenuItem>
            <DropdownMenuItem data-testid="action-print"><Printer className="w-4 h-4 mr-2" /> Imprimir</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowResetConfirm(true)} className="text-red-500 focus:text-red-600" data-testid="action-reset-menu"><Trash2 className="w-4 h-4 mr-2" /> Resetar Menu Completo</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-full sm:w-44 bg-white border-gray-200" data-testid="filter-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1 min-w-[140px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Buscar" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 w-full bg-white border-gray-200" data-testid="search-products" />
        </div>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-gray-500 text-xs uppercase">
              <th className="text-left p-3 font-medium w-10"></th><th className="text-left p-3 font-medium">Nombre</th><th className="text-left p-3 font-medium">Codigo</th>
              <th className="text-left p-3 font-medium">Categoria</th><th className="text-center p-3 font-medium">Canal</th><th className="text-center p-3 font-medium">Cantidad</th><th className="text-center p-3 font-medium">A la venta</th>
              <th className="text-center p-3 font-medium">Item de variacion</th><th className="text-left p-3 font-medium">Alergenos</th><th className="text-right p-3 font-medium">Precio</th><th className="p-3 w-28"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map(product => (
              <tr key={product.id} className="hover:bg-gray-50 transition-colors" data-testid={`product-row-${product.id.slice(-6)}`}>
                <td className="p-3">{product.image_url ? <img src={`${API_URL}${product.image_url}`} alt="" className="w-10 h-10 rounded object-cover" /> : product.video_url ? <div className="w-10 h-10 rounded bg-indigo-50 flex items-center justify-center" title="Video"><svg className="w-4 h-4 text-indigo-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg></div> : <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center"><Package className="w-4 h-4 text-gray-400" /></div>}</td>
                <td className="p-3"><span className="text-emerald-700 font-medium cursor-pointer hover:underline" onClick={() => { setEditProduct(product); setShowForm(true); }}>{product.name}</span></td>
                <td className="p-3 text-gray-600">{product.code || "-"}</td>
                <td className="p-3 text-gray-600">{getCatName(product.category_id)}</td>
                <td className="p-3 text-center">
                  <span className="flex gap-1 justify-center flex-wrap">
                    {product.show_in_delivery !== false && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-600">Delivery</span>}
                    {product.show_in_table !== false && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-600">Mesa</span>}
                    {product.show_in_pos !== false && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-purple-50 text-purple-600">POS</span>}
                  </span>
                </td>
                <td className="p-3 text-center text-gray-600">{product.quantity || 0}</td>
                <td className="p-3 text-center"><Switch checked={product.for_sale !== false} onCheckedChange={() => toggleForSale(product)} data-testid={`toggle-sale-${product.id.slice(-6)}`} /></td>
                <td className="p-3 text-center"><Switch checked={!!product.has_variation} onCheckedChange={() => toggleVariation(product)} className="data-[state=checked]:bg-emerald-500" data-testid={`toggle-var-${product.id.slice(-6)}`} /></td>
                <td className="p-3"><div className="flex gap-0.5 flex-wrap">{(product.allergens || []).map(a => { const al = ALLERGENS_DATA.find(x => x.id === a); return al ? <span key={a} className="text-xs" title={al.name}>{al.icon}</span> : null; })}{(!product.allergens || product.allergens.length === 0) && <span className="text-gray-300 text-xs">-</span>}</div></td>
                <td className="p-3 text-right">
                  {product.original_price > 0 && product.original_price > product.price && (
                    <span className="text-[10px] text-gray-400 line-through mr-1">{formatPrice(product.original_price)}</span>
                  )}
                  <span className="font-medium">{formatPrice(product.price)}</span>
                  {product.discount_percentage > 0 && (
                    <span className="ml-1 text-[10px] bg-red-100 text-red-600 px-1 py-0.5 rounded font-bold">-{product.discount_percentage}%</span>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex gap-0.5 justify-end">
                    {menuLinkBase && (
                      <ProductShareLink
                        product={product}
                        origin={menuLinkBase.origin}
                        restaurantSlug={menuLinkBase.restaurantSlug}
                      />
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-indigo-600" title="Clonar produto" onClick={() => { setEditProduct({ ...product, id: null, name: product.name + " (cópia)" }); setShowForm(true); }}><Copy className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => deleteProduct(product.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={11} className="p-8 text-center text-gray-400">No se han encontrado productos</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"><ProductFormDialog product={editProduct} categories={categories} onClose={(savedProduct) => {
          setShowForm(false);
          if (savedProduct) {
            if (editProduct?.id) {
              // Update existing product in list immediately
              setProducts(prev => prev.map(p => p.id === savedProduct.id ? savedProduct : p));
            } else {
              // Prepend new product (or clone) to list immediately
              setProducts(prev => [savedProduct, ...prev]);
            }
          } else {
            onRefresh();
          }
        }} onCategoriesRefresh={onRefresh} /></DialogContent>
      </Dialog>
      <BulkImageUpload open={showBulkUpload} onOpenChange={setShowBulkUpload} products={products} categories={categories} onRefresh={onRefresh} />
      <CloneMenuDialog open={showCloneMenu} onOpenChange={setShowCloneMenu} onRefresh={onRefresh} />
      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent className="max-w-md" data-testid="reset-menu-dialog">
          <div className="flex items-center gap-2 mb-3 text-red-600 font-bold">
            <AlertTriangle className="w-5 h-5" /> Resetar Menu Completo
          </div>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Esto eliminara <strong className="text-red-600">{products.length} productos</strong> y <strong className="text-red-600">{categories.length} categorias</strong> de tu restaurante.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-xs text-red-700 font-medium">Esta accion no se puede deshacer. Todos los productos, categorias, variaciones y combos seran eliminados permanentemente.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowResetConfirm(false)}>Cancelar</Button>
              <Button variant="destructive" disabled={resetting} data-testid="confirm-reset-btn" onClick={async () => {
                setResetting(true);
                try {
                  const res = await authAxios.delete("/menu/reset");
                  toast.success(`Menu resetado: ${res.data.products_deleted} productos y ${res.data.categories_deleted} categorias eliminados`);
                  setShowResetConfirm(false); onRefresh();
                } catch (err) { toast.error(err.response?.data?.detail || "Error al resetar menu"); }
                finally { setResetting(false); }
              }}>
                {resetting ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Eliminando...</> : "Si, Eliminar Todo"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
